import { StyledInput } from '@atoms/Input/Input.styled';
import { INPUT_TYPE } from '@atoms/Input/Input.types';
import Link from 'next/link';
import type { ReactElement, TransitionEvent } from 'react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';

import { Highlight } from '@/styles/shared';

import {
  CheckIcon,
  ChecklistGrid,
  ChecklistItem,
  CollapseInner,
  CollapseWrapper,
  ConfirmationMessage,
  ConsentLabel,
  ErrorMessage,
  FieldWrapper,
  FormCard,
  HoneypotWrapper,
  NoWrap,
  PrivacyNote,
  SubmitButton,
  Title,
} from './MapOptIn.styled';

interface MapaLeadFormData {
  consent: boolean;
  email: string;
  website: string;
}

type Phase = 'closing' | 'idle' | 'revealed';

const EMAIL_REGEX = /^\w+([-+.']\w+)*@\w+([-.]\w+)*\.\w+([-.]\w+)*$/i;

export const MAP_SUBSCRIBED_STORAGE_KEY = 'mapa_teneryfa_subscribed';

const checklistItems = [
  'Gotowa do użycia w Google Maps',
  'Plaże, restauracje, szlaki i widoki',
  'Dostęp od razu po zapisie',
];

interface MapOptInContentProps {
  compact?: boolean;
  headingLevel?: 'h1' | 'h2';
  onRevealed?: () => void;
}

export const MapOptInContent = ({
  compact = false,
  headingLevel = 'h1',
  onRevealed,
}: MapOptInContentProps): ReactElement => {
  const [phase, setPhase] = useState<Phase>('idle');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<MapaLeadFormData>();

  const submitForm = async (data: MapaLeadFormData): Promise<void> => {
    setIsSubmitting(true);
    setErrorMessage(null);

    try {
      const res = await fetch('/api/mapa-teneryfa', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      });

      if (!res.ok) {
        throw new Error('request_failed');
      }

      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        event: 'generate_lead',
        destination: 'teneryfa',
        lead_magnet: 'mapa',
      });

      localStorage.setItem(MAP_SUBSCRIBED_STORAGE_KEY, '1');
      setPhase('closing');
    } catch {
      setErrorMessage('Coś poszło nie tak. Spróbuj ponownie.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFormCollapseEnd = (
    event: TransitionEvent<HTMLDivElement>,
  ): void => {
    if (
      event.target !== event.currentTarget ||
      event.propertyName !== 'grid-template-rows' ||
      phase !== 'closing'
    ) {
      return;
    }

    setPhase('revealed');
    onRevealed?.();
  };

  return (
    <>
      <Title $compact={compact} as={headingLevel}>
        Darmowa mapa{' '}
        <NoWrap $wrap={compact}>
          <Highlight>170{compact ? ' ' : ' '}sprawdzonych</Highlight>
        </NoWrap>{' '}
        miejsc na Teneryfie
      </Title>

      <ChecklistGrid>
        {checklistItems.map(item => (
          <ChecklistItem key={item}>
            <CheckIcon aria-hidden="true">✅</CheckIcon>
            {item}
          </ChecklistItem>
        ))}
      </ChecklistGrid>

      {phase === 'revealed' ? (
        <ConfirmationMessage>📬 Sprawdź skrzynkę</ConfirmationMessage>
      ) : (
        <CollapseWrapper
          $open={phase === 'idle'}
          onTransitionEnd={handleFormCollapseEnd}
        >
          <CollapseInner>
            <FormCard onSubmit={handleSubmit(submitForm)}>
              <FieldWrapper>
                <label htmlFor="email">Email</label>
                <StyledInput
                  {...register('email', {
                    required: true,
                    pattern: EMAIL_REGEX,
                    setValueAs: (value: string) => value.trim(),
                  })}
                  id="email"
                  placeholder="Twój adres e-mail"
                  type={INPUT_TYPE.EMAIL}
                  aria-invalid={errors.email ? 'true' : 'false'}
                />
                {errors.email && (
                  <ErrorMessage>Podaj poprawny adres e-mail</ErrorMessage>
                )}
              </FieldWrapper>

              <div>
                <ConsentLabel htmlFor="consent">
                  <input
                    {...register('consent', { required: true })}
                    id="consent"
                    type="checkbox"
                  />
                  <span>
                    Zgadzam się na otrzymywanie treści marketingowych i ofert
                    Fox Paradox zgodnie z{' '}
                    <Link
                      href="/polityka-prywatnosci"
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      polityką prywatności
                    </Link>
                    .
                  </span>
                </ConsentLabel>
                {errors.consent && (
                  <ErrorMessage>Zgoda jest wymagana</ErrorMessage>
                )}
              </div>

              <HoneypotWrapper aria-hidden="true">
                <input
                  {...register('website')}
                  autoComplete="off"
                  tabIndex={-1}
                  type="text"
                />
              </HoneypotWrapper>

              <SubmitButton disabled={isSubmitting} type="submit">
                <span aria-hidden="true">➤</span>
                {isSubmitting ? 'Wysyłam...' : 'Wyślij mi mapę'}
              </SubmitButton>

              {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}
            </FormCard>
          </CollapseInner>
        </CollapseWrapper>
      )}

      {phase !== 'revealed' && (
        <PrivacyNote>
          🔒 Szanuję Twoją prywatność. Możesz wypisać się w każdej chwili.
        </PrivacyNote>
      )}
    </>
  );
};
